import { HOME_WIDGET_QUERY_META } from "@/lib/home/widgets/query-lifecycle";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import type { AdminQrCodeRow } from "@/lib/qr-codes/admin-types";
import type { QRListResponse, QRSortField, QRSortOrder } from "@/lib/qr-codes/types";

// Rows load in pages of 50 behind "Afficher plus": grouping by project needs
// whole groups, which 10-row pages would split. 50 covers nearly every user.
export const QR_CODES_PAGE_SIZE = 50;

/**
 * The user's PDF QR codes, latest change first — one page of 100 for the
 * home page's overview (total views) and "Récents".
 */
export const recentQrPdfQueryOptions = queryOptions({
  queryKey: ["qr-codes", "home"],
  meta: HOME_WIDGET_QUERY_META,
  queryFn: async ({ signal }): Promise<QRListResponse> => {
    const params = new URLSearchParams({
      page: "1",
      limit: "100",
      sort: "lastModified",
      order: "desc",
      fetchContext: "personal",
    });

    const res = await fetch(`/api/qr-codes?${params}`, { cache: "no-store", signal });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    // SAFETY: the qr-codes list route returns the { data, pagination } envelope.
    return (await res.json()) as QRListResponse;
  },
});

/** Rows per page of the admin QR code list. */
const ADMIN_QR_PAGE_SIZE = 100;

export interface AdminQrListResponse {
  data: AdminQrCodeRow[];
  pagination: { total: number; page: number; pageSize: number; pageCount: number };
}

export interface AdminQrCodesListParams {
  search: string;
  projectId: string;
  createdSince: string | null;
  sortBy: "name" | "createdAt" | "lastModified" | "totalViews" | "totalDownloads";
  sortOrder: "asc" | "desc";
}

/** The admin's paged list of QR codes (GET /api/admin/qr-codes). */
export function adminQrCodesListQueryOptions(params: AdminQrCodesListParams) {
  return infiniteQueryOptions({
    queryKey: [
      "admin",
      "qr-codes",
      "list",
      params.search.trim(),
      params.projectId,
      params.createdSince,
      params.sortBy,
      params.sortOrder,
    ],
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<AdminQrListResponse> => {
      const search = new URLSearchParams({
        page: String(pageParam),
        limit: String(ADMIN_QR_PAGE_SIZE),
        sort: params.sortBy,
        order: params.sortOrder,
      });

      if (params.search.trim()) search.append("search", params.search.trim());

      if (params.projectId) search.set("projectId", params.projectId);

      if (params.createdSince) search.set("createdSince", params.createdSince);
      const res = await fetch(`/api/admin/qr-codes?${search.toString()}`, { cache: "no-store" });

      if (!res.ok) throw new Error("Erreur lors du chargement des données");

      // SAFETY: /api/admin/qr-codes returns the AdminQrListResponse contract.
      return (await res.json()) as AdminQrListResponse;
    },
    getNextPageParam: (last) =>
      last.pagination.page < last.pagination.pageCount ? last.pagination.page + 1 : undefined,
  });
}

/**
 * The paged QR codes library for one filter, search and sort. The QR codes page
 * and its route loader share this factory, so the loader's warm read is the
 * one the page mounts with.
 */
export function qrCodesListQueryOptions({
  filterProject = "all",
  search = "",
  sort = "createdAt",
  order = "desc",
}: {
  /** "all", "personal_only" or a project id. */
  filterProject?: string;
  search?: string;
  sort?: QRSortField;
  order?: QRSortOrder;
} = {}) {
  return infiniteQueryOptions({
    queryKey: ["qr-codes", filterProject, search, sort, order],
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<QRListResponse> => {
      const params = new URLSearchParams({
        page: String(pageParam),
        limit: String(QR_CODES_PAGE_SIZE),
        sort,
        order,
      });

      if (search) params.set("search", search);

      if (filterProject === "personal_only") params.set("personalOnly", "true");
      else if (filterProject !== "all") params.set("projectId", filterProject);
      const res = await fetch(`/api/qr-codes?${params.toString()}`, { cache: "no-store" });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // SAFETY: the qr-codes list route returns the { data, pagination }
      // envelope built server-side.
      return (await res.json()) as QRListResponse;
    },
    getNextPageParam: (last) =>
      last.pagination.page < last.pagination.pageCount ? last.pagination.page + 1 : undefined,
  });
}
